/**
 * Code-based TanStack Router tree. WP3.1 added the shell screens, WP3.2 the catalog
 * (`/songs`, `/songs/new`, `/songs/$songId`, `/artists`), WP3.3 the editorial screens
 * (`/playlists`, `/heroes`, `/home`), WP3.4 the operations ones (`/access`, `/users`,
 * `/devices`, `/sessions`) and WP3.5 the catalog review queue (`/review`).
 * Each screen also flips its sidebar entry on
 * (components/layout/nav.ts).
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  type RouterHistory,
} from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { atLeast, useAuth } from './auth/AuthProvider';
import { AppShell, PageHeader } from './components/layout/AppShell';
import { useT } from './i18n';
import { Access } from './screens/Access';
import { Artists } from './screens/Artists';
import { AuditLog } from './screens/AuditLog';
import { CatalogReview } from './screens/CatalogReview';
import { Dashboard } from './screens/Dashboard';
import { Devices } from './screens/Devices';
import { Heroes } from './screens/Heroes';
import { HomeEditorial } from './screens/HomeEditorial';
import { NewSong } from './screens/NewSong';
import { Playlists } from './screens/Playlists';
import { Sessions } from './screens/Sessions';
import { Settings } from './screens/Settings';
import { SongEditor } from './screens/SongEditor';
import { Songs } from './screens/Songs';
import { Users } from './screens/Users';

function NotFound() {
  const { t } = useT();
  return <PageHeader title={t('common:errors.not_found')} />;
}

/** In-app guard for the admin-only screens; the API enforces the same rule (docs/03 §1). */
function RequireAdmin({ children }: { children: ReactNode }) {
  const { t } = useT();
  const { role } = useAuth();
  if (!atLeast(role, 'admin')) return <PageHeader title={t('common:errors.forbidden')} />;
  return <>{children}</>;
}

/** Same, for the editor-only screens (creating a song, editing an artist). */
function RequireEditor({ children }: { children: ReactNode }) {
  const { t } = useT();
  const { role } = useAuth();
  if (!atLeast(role, 'editor')) return <PageHeader title={t('common:errors.forbidden')} />;
  return <>{children}</>;
}

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
  notFoundComponent: NotFound,
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Dashboard,
});

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit',
  component: AuditLog,
});

const songsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/songs',
  component: Songs,
});

const newSongRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/songs/new',
  component: () => (
    <RequireEditor>
      <NewSong />
    </RequireEditor>
  ),
});

const songEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/songs/$songId',
  component: function SongEditorRoute() {
    const { songId } = songEditorRoute.useParams();
    return <SongEditor songId={songId} />;
  },
});

const artistsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/artists',
  component: Artists,
});

const playlistsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/playlists',
  component: Playlists,
});

const heroesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/heroes',
  component: Heroes,
});

const homeEditorialRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/home',
  component: HomeEditorial,
});

const accessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/access',
  component: Access,
});

const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  component: () => (
    <RequireAdmin>
      <Users />
    </RequireAdmin>
  ),
});

const devicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/devices',
  component: Devices,
});

const sessionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sessions',
  component: Sessions,
});

const reviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/review',
  component: CatalogReview,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: () => (
    <RequireAdmin>
      <Settings />
    </RequireAdmin>
  ),
});

export const routeTree = rootRoute.addChildren([
  dashboardRoute,
  songsRoute,
  newSongRoute,
  songEditorRoute,
  artistsRoute,
  playlistsRoute,
  heroesRoute,
  homeEditorialRoute,
  accessRoute,
  usersRoute,
  devicesRoute,
  sessionsRoute,
  reviewRoute,
  auditRoute,
  settingsRoute,
]);

export function createCmsRouter(history?: RouterHistory) {
  return createRouter({ routeTree, ...(history ? { history } : {}) });
}

export type CmsRouter = ReturnType<typeof createCmsRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: CmsRouter;
  }
}
